import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { useRefetchOnReturn } from '@/hooks/useRefetchOnReturn';
import { useAuthStore } from '@/stores/authStore';
import { BookFooter } from './BookFooter';
import { BookHeader } from './BookHeader';
import { BookLane } from './BookLane';
import { BookSideImages } from './BookSideImages';
import { Schrittleiste } from './Schrittleiste';
import { VerifyReminderLine } from './VerifyReminderLine';
import { deriveBookSlot } from '../book-slot';
import { useReentryStep } from '../use-reentry-step';
import { BookShellContextProvider } from '../book-shell-context-provider';
import type { BookShellContext } from '../types';

// Re-export for callers that already knew the interface's original
// home. The canonical declaration lives in `../types`.
export type { BookShellContext } from '../types';

/**
 * P5-4 Commit A — the book flow's shell layout route.
 *
 * Per Order R2: the book flow gets its OWN shell. No touch to
 * AppShell.tsx, OnboardingGuard.tsx or ProtectedRoute.tsx.
 * Registered as a layout route inside the third sibling <Route>
 * under ProtectedRoute at pwa/src/App.tsx (see the P5-4 Commit A
 * route registration).
 *
 * Deliberately does NOT render:
 *   - TopBar (audio-side shell chrome; Bau-Spez §1 G2 forbids
 *     the audio tab bar during the six book steps)
 *   - BottomNav (same reason)
 *   - MiniPlayer (audio-side; no book equivalent)
 *   - WorldSelectionSheet (audio-side world picker; book has no
 *     world dimension)
 *
 * DOES render since 5a Commit 4 (Sebastian ruling 2026-08-06):
 *   - `VerifyReminderLine` — a quiet reminder + resend action when
 *     the authenticated customer has NOT yet verified his email.
 *     No gate, no modal — the verification pressure sits at
 *     Phase-6 checkout (`checkout.controller.ts:22` uses
 *     `EmailVerifiedGuard`); until then he should be building.
 *     Renders as the second row of chrome, above the lane.
 *     Wording is placeholder pending Sebastian's authorship; see
 *     `STRINGS.book.shell.verifyReminderLine` for the register.
 *
 * Composition:
 *   BookHeader (Kopfleiste + optional Schrittleiste slot per §5)
 *   + BookLane wrapping <Outlet /> (the 680 px lane per §2 K-02)
 *
 * The lane wraps the outlet — every book route renders INTO the
 * lane, so the "eine Breite für alle sechs Schritte" invariant
 * (spec §2) is enforced at the shell layer, not each route.
 *
 * P5-4 Commit B — Schrittleiste wiring. The shell reads `orderId`
 * from `useMatch('/book/:orderId/*')`. Match-based (not
 * `useParams()` alone) so the shell is unambiguously scoped to the
 * parameterised route: on the bare `/book` placeholder route,
 * `useMatch` returns null and the shell skips the fetch entirely.
 *
 * Splat form 2026-09-09 fix (Sebastian ruling — Bestellübersicht
 * slice fallout). The prior exact-match pattern `'/book/:orderId'`
 * matched ONLY the bare parameterised route; any cold-boot to a
 * nested authenticated URL — `/book/:orderId/bestellung`,
 * `/book/:orderId/erfolg` — returned null from useMatch, produced
 * `orderId=null`, and `useReentryStep(null)` skipped the envelope
 * fetch. The Outlet then rendered a leaf route (BestellungRoute /
 * BestellungErfolgRoute) whose `envelope === null` guard returned
 * null forever; nothing threw, nothing surfaced, the customer saw
 * a blank content area under a headered shell without the
 * Schrittleiste. The bug pre-dated the Bestellübersicht slice —
 * customers rarely cold-reload deep book URLs, so the wiring flaw
 * stayed dormant until the reload-safe phase param gave them a
 * reason to. The `/*` splat captures orderId on any nested path
 * (`/book/abc` and `/book/abc/bestellung` both match with
 * orderId='abc'; the bare `/book` still yields null and the fetch
 * still skips) so cold-boot at every nested route fetches the
 * envelope on mount.
 *
 * The fetch happens here at shell level, not inside `BookRoute`,
 * because the Schrittleiste is a shell chrome element (visually the
 * second row of the header per §5 "zweite Kopfzeile"). Colocating
 * the fetch with the presentation avoids the outlet-to-shell state
 * propagation problem (React context flows down, not up; storing
 * the step in a global store would introduce a synchronous setState
 * in an effect body — the same lint-rule failure mode named at
 * tracker `[pwa-lint-debt-set-state-in-effect]`).
 *
 * `BookRoute` reads the envelope via `useBookShellContext()`
 * (`../book-shell-context.tsx`), a plain React context wrapped
 * around the outlet below. The prior wire used
 * `<Outlet context={...}>` + `useOutletContext`, which broke silently
 * because the real production route tree nests ProtectedRoute
 * between BookShell and BookRoute, and React Router v7 does not
 * propagate outlet context through nested Outlets — see the JSDoc
 * on `BookShellContextMissingError` for the full incident write-up.
 *
 * Block 1 (Sebastian ruling 2026-09-09) — the book shell now paints
 * the whole flow on the warm-paper palette (`bg-book-page`,
 * #F4F1EA), the content lives inside a white card (see BookLane),
 * and two decorative side-image panels (BookSideImages) sit at the
 * viewport edges outside the centre lane. Applied here at shell
 * level so every book screen inherits the same visual system
 * without touching leaf routes. Audio side is unaffected — the
 * default body `bg-background` (#fff7e4) is overridden only inside
 * this component's outer div.
 */
export function BookShell() {
  const match = useMatch('/book/:orderId/*');
  const accessToken = useAuthStore((s) => s.accessToken);
  // BookShell sits OUTSIDE the nested ProtectedRoute (see App.tsx), so
  // this layout mounts for anonymous visitors arriving at a protected
  // /book/:orderId URL — e.g. the Light-Gate verify-link click opens
  // in a mail app's in-app browser without the SPA's persisted session.
  // The nested <ProtectedRoute> redirects to /auth/login in the same
  // render pass, but a non-null orderId here would still fire the
  // envelope fetch before the redirect, producing a surprise 401 (Sentry
  // issue cda96bf13aa646d392df6c9e2d7e0f25). Gate on accessToken so the
  // fetch only leaves when there is a session; usePreviewEnvelope
  // no-ops on orderId=null, and ProtectedRoute's returnTo brings the
  // customer back after login.
  const orderId = accessToken ? (match?.params.orderId ?? null) : null;
  const { step, envelope, loading, error, refetch } = useReentryStep(orderId);
  const user = useAuthStore((s) => s.user);
  const showVerifyReminder = user !== null && user.emailVerified === false;
  const context: BookShellContext = { envelope, loading, error, refetch };

  // Sebastian ruling 2026-10-22 — refetch-on-return at shell level.
  // Covers every book sub-route (/book/:orderId and nested, incl.
  // /bestellung + /erfolg) through the single envelope fetch. The
  // originating scenario: customer opens /book/:orderId/bestellung in
  // tab A, pays in Stripe tab B, backend flips the order to paid.
  // Switching back to tab A re-renders Bestellung with a stale
  // envelope — without this hook the customer must hand-reload or
  // wait on the status's polling cadence (checkout_pending is NOT in
  // the polling list, so there would be no cadence). With this hook,
  // the return event fires one refetch (throttled 5s) and
  // BookRoute's status switch flips to PostPaymentWaitScreen on its
  // own. `orderId === null` on the configurator route means refetch
  // is a no-op by construction (see usePreviewEnvelope.refetch),
  // which is the right behaviour there — nothing to refresh.
  //
  // 5000 ms matches NON_TERMINAL_POLLING_STATES cadence; a browser
  // that double-fires visibilitychange + focus on return (common on
  // macOS) emits both inside the throttle window and only triggers
  // one fetch.
  useRefetchOnReturn({ onReturn: refetch, throttleMs: 5000 });

  const location = useLocation();
  const slot = deriveBookSlot(envelope?.status ?? null, location.pathname);
  // URL-driven Schrittleiste overrides (Sebastian rulings):
  //   Block 2 · 2.2 (2026-09-09) — `/book` (configurator, no
  //     orderId) pins step 1 (Personen). Envelope-less path.
  //   Block 6 · 6.5 (2026-09-09) — `/book/:id/bestellung` (tile
  //     selection AND Bestellübersicht review) pins step 5
  //     (Bestellung). Fixes the pre-Block-6 defect where the
  //     Schrittleiste showed "Vorschau" as active on the review
  //     screen because the order status is still preview_ready
  //     (status flips to checkout_pending only after the Kaufen
  //     click). URL is the truthful signal for which screen the
  //     customer is on.
  const path = location.pathname;
  let effectiveStep = step;
  if (path.endsWith('/bestellung')) {
    // Force Bar A · mark 5 (Bestellung). Overrides envelope status
    // whether that says preview_ready, checkout_pending, or anything
    // else Bar-A-mapped; the customer is looking at the bestellung
    // screen, that is the truth.
    effectiveStep = { kind: 'step', value: 5 };
  } else if (effectiveStep === null && path === '/book') {
    effectiveStep = { kind: 'step', value: 1 };
  } else if (path.endsWith('/review')) {
    // Book-arrived review page (feat/book-review-followup, Sebastian
    // 2026-10-21) — the Schrittleiste belongs to the configurator /
    // production flow and would confuse a customer who already
    // received his book and is now rating + sharing. Hide it on
    // this route only; the top bar (logo, Meine Bücher, Passwort
    // ändern, Abmelden) stays.
    effectiveStep = null;
  }
  return (
    <div className="relative min-h-screen bg-book-page">
      <BookHeader
        schrittleiste={effectiveStep ? <Schrittleiste step={effectiveStep} /> : undefined}
      />
      {showVerifyReminder && <VerifyReminderLine />}
      <BookSideImages slot={slot} />
      {/* `relative z-10` keeps the main column above the fixed side
          images. The panels sit at z-0 and are pointer-events-none,
          so this z-index guards focus/tap targets even if a future
          side image contains an anchor by accident. */}
      <main className="relative z-10">
        <BookLane>
          <BookShellContextProvider value={context}>
            <Outlet />
          </BookShellContextProvider>
        </BookLane>
      </main>
      <BookFooter />
    </div>
  );
}
